import { ApexOptions } from 'apexcharts'
import BaseChartCard from 'common/components/BaseChartCard'
import CustomChartTooltip from 'common/components/CustomChartTooltip'
import DateRangePicker, { DateRange } from 'common/components/DateRangePicker'
import MultipleSelect from 'common/components/MultipleSelect'
import { FeatureKey } from 'common/constants/permission'
import { formatThaiShortDateTwoDigitYear, getTodayDate } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { isSameDay } from 'date-fns'
import { useGetPondDashboardLineChart } from 'modules/pond/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import { usePermitByFeature } from 'modules/user/api/selector'
import { useState, useMemo } from 'react'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'

import { Box, Stack } from '@mui/material'

import DashboardLineChart from '../DashboardLineChart'
import './style.css'

const isDefaultDateRange = (range: DateRange | null): boolean => {
  if (!range?.startDate || !range?.endDate) return true
  const now = new Date()
  return (
    range.startDate.toDateString() === now.toDateString() &&
    range.endDate.toDateString() === now.toDateString()
  )
}

const DashboardChart = () => {
  const { t } = useTranslation(['pond', 'common', 'admin', 'food', 'growth', 'deadshrimp', 'water'])
  const speciesName = useSpeciesName()

  const allowFood = usePermitByFeature(FeatureKey.Food)
  const allowGrowth = usePermitByFeature(FeatureKey.Growth)
  const allowDeath = usePermitByFeature(FeatureKey.Death)
  const allowQuality = usePermitByFeature(FeatureKey.WaterQuality)

  const checklistItems = useMemo(
    () => [
      {
        id: 1,
        value: t('food:consuming.food.chart.title.short'),
        graphParam: 'food',
        qualityParam: null,
        color: '#91CAFF',
        unit: t('food:kilo'),
        feature: allowFood,
      },
      {
        id: 2,
        value: t('pond:growth'),
        graphParam: 'growth',
        qualityParam: null,
        color: '#00AB55',
        unit: t('pond:gram.per.one'),
        feature: allowGrowth,
      },
      {
        id: 3,
        value: t('pond:dead.shrimp'),
        graphParam: 'mortality',
        qualityParam: null,
        color: '#FF6B6B',
        unit: t('food:kilo'),
        feature: allowDeath,
      },
      {
        id: 4,
        value: t('water:pH'),
        graphParam: 'water_quality',
        qualityParam: 'ph',
        color: '#FFD93D',
        unit: '',
        feature: allowQuality,
      },
      {
        id: 5,
        value: t('pond:pond.do'),
        graphParam: 'water_quality',
        qualityParam: 'do',
        color: '#6BCB77',
        unit: 'ppm',
        feature: allowQuality,
      },
      {
        id: 6,
        value: t('water:temperature'),
        graphParam: 'water_quality',
        qualityParam: 'temp',
        color: '#4D96FF',
        unit: '°C',
        feature: allowQuality,
      },
      {
        id: 7,
        value: t('water:ammonia'),
        graphParam: 'water_quality',
        qualityParam: 'ammonia',
        color: '#FF6EC7',
        unit: 'ppm',
        feature: allowQuality,
      },
      {
        id: 8,
        value: t('water:nitrite'),
        graphParam: 'water_quality',
        qualityParam: 'nitrite',
        color: '#FFB347',
        unit: 'ppm',
        feature: allowQuality,
      },
      {
        id: 9,
        value: t('water:alkali'),
        graphParam: 'water_quality',
        qualityParam: 'alkali',
        color: '#9D4EDD',
        unit: 'ppm',
        feature: allowQuality,
      },
      {
        id: 10,
        value: t('water:turbidity'),
        graphParam: 'water_quality',
        qualityParam: 'turbidity',
        color: '#00C2A0',
        unit: 'ppt',
        feature: allowQuality,
      },
    ],
    [t, allowFood, allowGrowth, allowDeath, allowQuality]
  )

  const allowedChecklistItems = useMemo(
    () => checklistItems.filter((item) => item.feature),
    [checklistItems]
  )

  const defaultSelected = allowedChecklistItems.slice(0, 2).map((i) => i.id)

  const [selectedFilter, setSelectedFilter] = useState<(number | string)[]>(defaultSelected)

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })

  const selectedItems = allowedChecklistItems.filter((item) => selectedFilter.includes(item.id))

  // Unique graph types from selected items (e.g. ['food', 'water_quality'])
  const graphsParam = [...new Set(selectedItems.map((item) => item.graphParam))].join(',')

  // Quality sub-types from selected items that are water quality metrics
  const qualityParam = selectedItems
    .filter((item) => item.qualityParam !== null)
    .map((item) => item.qualityParam)
    .join(',')

  const formatDateForApi = (date: Date) => date.toISOString().split('T')[0]

  const {
    data: chart,
    isFetching: isChartFetching,
    refetch: refetchChart,
  } = useGetPondDashboardLineChart({
    graphs: graphsParam.length > 0 ? graphsParam : undefined,
    startDate:
      dateRange.startDate && !isDefaultDateRange(dateRange)
        ? formatDateForApi(dateRange.startDate)
        : undefined,
    endDate:
      dateRange.endDate && !isDefaultDateRange(dateRange)
        ? formatDateForApi(dateRange.endDate)
        : undefined,
    quality: qualityParam.length > 0 ? qualityParam : undefined,
  })

  type DataPoint = { shrimpAge: number; value: number }

  const chartData = {
    shrimpGrowthLineChart: (chart?.shrimpGrowthLineChart || []) as DataPoint[],
    foodLineChart: (chart?.foodLineChart || []) as DataPoint[],
    waterQualityLineChart: (chart?.waterQualityLineChart || {}) as Record<string, DataPoint[]>,
    mortalityLineChart: (chart?.mortalityLineChart || []) as DataPoint[],
  }

  const graphParamToChartKey: Record<
    string,
    'foodLineChart' | 'shrimpGrowthLineChart' | 'mortalityLineChart'
  > = {
    food: 'foodLineChart',
    growth: 'shrimpGrowthLineChart',
    mortality: 'mortalityLineChart',
  }

  const getDataPoints = (item: (typeof checklistItems)[number]): DataPoint[] => {
    if (item.qualityParam !== null) {
      return chartData.waterQualityLineChart[item.qualityParam] || []
    }
    const chartKey = graphParamToChartKey[item.graphParam] as
      | 'foodLineChart'
      | 'shrimpGrowthLineChart'
      | 'mortalityLineChart'
    return chartData[chartKey] || []
  }

  // Apex anchors the tooltip to the top-left corner when the first series is empty
  // (e.g. no food recorded yet in a new cycle), so series with data go first.
  const seriesItems = [...selectedItems].sort(
    (a, b) => Number(getDataPoints(b).length > 0) - Number(getDataPoints(a).length > 0)
  )

  const chartColors = seriesItems.map((item) => item.color)

  const chartSeries = seriesItems.map((item, index) => {
    const dataPoints = getDataPoints(item)
    return {
      name: item.value,
      data: dataPoints.map((point) => ({ x: point.shrimpAge, y: point.value })),
      yAxis: index === 0 ? 0 : 1,
    }
  })

  const chartOptions: ApexOptions = {
    colors: chartColors,
    yaxis: seriesItems.map((item, index) => ({
      title: { text: item.value },
      opposite: index === 1,
    })),
    grid: {
      padding: {
        right: 30,
      },
    },
    xaxis: {
      title: {
        text: t('food:shrimp.age.day', { speciesName }),
      },
      type: 'numeric',
      labels: { rotate: -45 },
      tickAmount: 6,
    },
    tooltip: {
      shared: false,
      intersect: false,
      custom: ({
        dataPointIndex,
        seriesIndex,
        w,
      }: {
        dataPointIndex: number
        seriesIndex: number
        w: { globals: { seriesX: number[][]; seriesNames: string[] } }
      }) => {
        const orderedItems = selectedItems.map((item) => ({
          item,
          data: getDataPoints(item),
        }))

        // With intersect: false, Apex may report an empty series (e.g. no food yet in a new
        // cycle), so fall back to the first series that has a point at this index.
        const shrimpAge =
          w.globals.seriesX[seriesIndex]?.[dataPointIndex] ??
          w.globals.seriesX.map((xs) => xs?.[dataPointIndex]).find((x) => x !== undefined)
        if (shrimpAge === undefined) return ''

        const tooltipData = orderedItems
          .map(({ item, data }) => {
            const val =
              data.find((el: { shrimpAge: number; value: number }) => el.shrimpAge === shrimpAge)
                ?.value ?? null
            if (val === null) return null
            return {
              title: item.value,
              value: `${fNumber(val)} ${item.unit}`,
            }
          })
          .filter((d): d is { title: string; value: string } => d !== null)
        if (tooltipData.length === 0) return ''

        return renderToString(
          <CustomChartTooltip
            title={`${t('food:shrimp.age', { speciesName })}: ${shrimpAge} ${t('day')}`}
            data={tooltipData}
          />
        )
      },
    },
  }

  const handleSelect = (values: (number | string)[]) => {
    if (values.length <= 2) {
      setSelectedFilter(values)
    }
  }

  const onDateRangeChange = (range: DateRange) => setDateRange(range)

  const dashboardDateDisplayValue = (startDate: Date | null, endDate: Date | null) => {
    if (!startDate || !endDate) return t('farm:please.select.date')

    const now = new Date()
    const isDefault =
      startDate.toDateString() === now.toDateString() &&
      endDate.toDateString() === now.toDateString()

    if (isDefault) return t('farm:please.select.date')

    return `${formatThaiShortDateTwoDigitYear(startDate)} - ${
      isSameDay(endDate, new Date()) ? t('pond:present') : formatThaiShortDateTwoDigitYear(endDate)
    }`
  }

  return (
    <BaseChartCard title={t('pond:info.graph')} refresh={refetchChart}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          py: 2,
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <MultipleSelect
          selectedValues={selectedFilter}
          onSelect={handleSelect}
          menuItems={allowedChecklistItems}
          width="250px"
          clearLabel={`${t('common:delete')}${t('admin:all')}`}
          maxSelect={2}
        />
        <Box className="dashboard-chart-datepicker">
          <DateRangePicker
            value={dateRange}
            onChange={onDateRangeChange}
            minDate={new Date(1900, 0, 1)}
            maxDate={getTodayDate()}
            showLabel={false}
            displayValue={dashboardDateDisplayValue}
          />
        </Box>
      </Stack>

      <DashboardLineChart
        yData={chartSeries}
        isLoading={isChartFetching}
        options={chartOptions}
        dataLength={chartSeries.flatMap((s) => s.data).length}
      />
    </BaseChartCard>
  )
}

export default DashboardChart
